<template>
  <div class="bots-dropdown-wrapper" :class="{ 'bots-light-bg': lightBackground }">
    <VSearchableSelect
      :modelValue="modelValue ? String(modelValue) : ''"
      @update:modelValue="handleSelection"
      :placeholder="label || 'Select value'"
      :loading="isLoading"
      fixed-dropdown
      appearance="ghost"
      class="bots-select"
    >
      <VOption
        v-for="bot in filteredBots"
        :key="bot.id"
        :value="String(bot.id)"
        :text="bot.displayname || bot.name"
        :label="bot.displayname || bot.name"
      />
      <template #loading-options>
        <span>Loading bot flows...</span>
      </template>
      <template #no-matches>
        <VEmptyState icon="search-line" headline="No bot flows found!" />
      </template>
    </VSearchableSelect>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watchEffect } from 'vue'
import { useUIStore } from '@/stores/ui'
import apiInterceptor from '@/shared/apiInterceptor'
import { environment } from '/src/shared/env'
import { queryClient } from '@/shared/queryClient';
import { botsQueryOptions } from '@/composables/useBots';

const updateToast = (payload) => useUIStore().updateToast(payload);

// Props and emits
const props = defineProps({
  modelValue: [String, Number],
  searchPlaceholder: [String, Number],
  label: [String, Number],
  apiUrl: { type: String, required: true },
  searchable: { type: Boolean, default: false },
  serverSearch: { type: Boolean, default: false },
  lightBackground: { type: Boolean, default: false },
  alwaysRefetch: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'change'])

// State
const allBots = ref([])
const isLoading = ref(false)

const BOTS_LIST_PREFIX = 'bot-builder/v1/bots/';

const fetchBots = async ({ searchText = '', forceNetwork = false } = {}) => {
  isLoading.value = true
  try {
    // Use TanStack Query cache for the standard bots list endpoint
    if (!props.serverSearch && props.apiUrl.startsWith(BOTS_LIST_PREFIX)) {
      const queryStr = props.apiUrl.slice(BOTS_LIST_PREFIX.length).replace(/^\?/, '');
      const urlParams = new URLSearchParams(queryStr);
      const channel = urlParams.get('sales_channel') || null;
      const isActive = urlParams.has('is_active');
      const queryOptions = botsQueryOptions({ channel, isActive });

      // Ensure the latest bot list is fetched when explicitly requested.
      if (forceNetwork || props.alwaysRefetch) {
        await queryClient.invalidateQueries({ queryKey: queryOptions.queryKey, exact: true });
      }

      allBots.value = await queryClient.fetchQuery(queryOptions);
    } else {
      let url = props.apiUrl
      if (props.serverSearch && searchText) {
        url += `?q=${encodeURIComponent(searchText)}`
      }
      const response = await apiInterceptor.get(environment('VUE_APP_API_URL') + url)
      const data = response.data || response.bots
      allBots.value = data
    }
  } catch (err) {
    console.error('Failed to fetch bots:', err)
    updateToast({
      open: true,
      message: err.message || 'Failed to fetch bots',
      type: "danger"
    })
  } finally {
    isLoading.value = false
  }
}

const filteredBots = computed(() => {
  return allBots.value || []
})

const handleSelection = (value) => {
  const bot = allBots.value?.find(a => String(a.id) === value)
  if (bot) {
    emit('update:modelValue', bot.id)
    emit('change', bot)
  }
}

const refresh = () => fetchBots({ forceNetwork: true })

defineExpose({ refresh })

watchEffect(() => {
  if (props.serverSearch) fetchBots();
})

// Init
onMounted(() => {
  fetchBots()
})
</script>

<style scoped>
.bots-dropdown-wrapper {
  width: 100%;
}
.bots-select {
  background: var(--background-primary);
  border-radius: 5px;
  min-height: 40px;
  width: 100%;
}
.no-results-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  text-align: center;
}
.no-results-wrapper h5 {
  margin: 10px 0 5px;
}
.text-medium {
  color: var(--gray-04);
  font-size: 13px;
}
</style>

<style>
.bots-dropdown-wrapper.bots-light-bg .bots-select {
  background: #fff !important;
}
</style>
